/** Persistent DOM HUD per mode: top bar (currencies), map actions, shop upgrade sheet, play bar. */
import type { JSX } from 'preact';
import { shopRate, xpProgress } from '@mp/core';
import { economy, rareItemDef, shopDef } from '../config/content.js';
import { t, has } from '../i18n/index.js';
import { upgradeCount, upgradeState } from '../services/progress.js';
import type { PlayerState } from '../services/state.js';
import type { MapHud, PlayHud, RailEntry, ShopHud } from './bridge.js';
import { Amount, Button } from './components.js';
import { DecoratePanel, DecorateButton } from './decor-hud.js';
import { Back, Calendar, Cart, Check, Friends, Gear, Lock, Mail, Pause, Play, Plus, Shop, Trophy, Wheel } from './icons.js';
import { Sprite } from './sprite.js';

export function TopBar(p: { s: PlayerState; pulse: number; onSettings: () => void; onStore?: (() => void) | null; onBack?: (() => void) | null }): JSX.Element {
  const xp = xpProgress(economy.xp.table, p.s.xp);
  return (
    <div class="topbar">
      {p.onBack ? (
        <button type="button" class="icon-btn" aria-label={t('hud.back')} onClick={p.onBack} data-testid="hud-back">
          <Back />
        </button>
      ) : (
        <div class="lvl-badge" aria-label={t('hud.playerLevel', { n: xp.level })}>
          <b>{xp.level}</b>
          <i style={{ width: `${Math.round((xp.into / xp.span) * 100)}%` }} />
        </div>
      )}
      <div class="pills">
        <span class={`pill ${p.pulse ? 'bump' : ''}`} key={`c${p.pulse}`} data-testid="hud-coins">
          <Amount kind="coins" value={p.s.coins} />
        </span>
        {p.onStore ? (
          <button type="button" class="pill pill-btn" data-testid="hud-gems" aria-label={t('store.title')} onClick={p.onStore}>
            <Amount kind="gems" value={p.s.gems} />
            <i class="pill-plus">
              <Plus size={12} />
            </i>
          </button>
        ) : (
          <span class="pill" data-testid="hud-gems">
            <Amount kind="gems" value={p.s.gems} />
          </span>
        )}
        <span class="pill" data-testid="hud-stars">
          <Amount kind="stars" value={p.s.stars} />
        </span>
      </div>
      <button type="button" class="icon-btn" aria-label={t('hud.settings')} onClick={p.onSettings} data-testid="hud-settings">
        <Gear />
      </button>
    </div>
  );
}

const RAIL_ICONS: Record<string, (p: { size?: number }) => JSX.Element> = { calendar: Calendar, trophy: Trophy, friends: Friends, wheel: Wheel, mail: Mail, cart: Cart };

function Rail({ items }: { items: RailEntry[] }): JSX.Element | null {
  if (!items.length) return null;
  return (
    <nav class="map-rail" aria-label={t('map.features')}>
      {items.map((r) => {
        const Icon = typeof r.icon === 'function' ? r.icon : RAIL_ICONS[r.icon];
        return (
          <button type="button" class="rail-btn" key={r.id} onClick={r.onOpen} data-testid={r.testId} aria-label={t(r.label)}>
            {Icon ? <Icon size={30} /> : <Sprite id={r.icon as string} size={30} />}
            <span>{t(r.label)}</span>
            {r.badge !== null && r.badge !== false && r.badge !== 0 && <i class="badge">{typeof r.badge === 'number' ? r.badge : ''}</i>}
          </button>
        );
      })}
    </nav>
  );
}

export function MapActions({ m }: { m: MapHud }): JSX.Element {
  return (
    <div class="map-actions">
      <div class="map-top">{m.offline && <div class="offline-chip">{t('map.offline')}</div>}</div>
      <Rail items={m.rail} />
      <div class="map-row">
        <button type="button" class={`shop-btn ${m.shopHint ? 'hint' : ''}`} onClick={m.onShop} aria-label={t('map.myShop')} data-testid="map-shop">
          <Shop size={34} />
          <span>{t('map.myShop')}</span>
          {m.shopHint && <i class="dot" />}
        </button>
        <Button variant="primary" size="lg" pulse onClick={m.onPlay} class="play-btn" testId="map-play" disabled={m.level > m.maxLevel}>
          <Play size={28} />
          <span class="play-label">
            <b>{m.level > m.maxLevel ? t('map.cityDone') : t('map.play')}</b>
            {m.level <= m.maxLevel && <small>{t('hud.level', { n: m.level })}</small>}
          </span>
        </Button>
      </div>
    </div>
  );
}

function upgradeName(id: string, fallback: string): string {
  const key = `upg.${id}`;
  return has(key) ? t(key) : fallback;
}

/** The first shop carries the player's chosen name (Flow A step I). */
export function shopTitle(s: PlayerState, shopId: string, fallback: string): string {
  if (s.shopName && shopId === 'ny_bagel') return s.shopName;
  const key = `shop.${shopId}.name`;
  return has(key) ? t(key) : fallback;
}

/** Asset id of an upgrade piece (e.g. shp_ny_bagel_upg01_b). */
function pieceId(shopId: string, slot: string, style: string): string {
  return shopId.replace(/^([a-z]+)_/, 'shp_$1_') + `_${slot}_${style}`;
}

function rareName(id: string): string {
  return has(`rare.${id}`) ? t(`rare.${id}`) : (rareItemDef(id)?.name ?? id);
}

function RareChip({ id, have }: { id: string; have: boolean }): JSX.Element {
  return (
    <span class={`rare-chip ${have ? 'have' : ''}`} title={rareName(id)}>
      <Sprite id={id} size={22} />
      {have ? <Check size={14} /> : <b>{rareName(id)}</b>}
    </span>
  );
}

export function ShopSheet({ h, s }: { h: ShopHud; s: PlayerState }): JSX.Element {
  const shop = shopDef(h.shopId)!;
  const count = upgradeCount(s, shop.id);
  if (h.visit)
    return (
      <div class="shop-sheet visit" data-testid="shop-sheet">
        <div class="sheet-head">
          <div>
            <h2>{t('friends.shopOf', { name: h.visit.owner })}</h2>
            <small>{t('shop.progress', { n: count, total: shop.upgrades.length })}</small>
          </div>
          {h.visit.trophy && (
            <span class="trophy-chip" data-testid="visit-trophy">
              <Trophy size={22} /> {t('friends.trophy')}
            </span>
          )}
        </div>
      </div>
    );
  if (h.decorate?.active) return <DecoratePanel d={h.decorate} />;
  return (
    <div class="shop-sheet" data-testid="shop-sheet">
      <div class="sheet-head">
        <div>
          <h2>{shopTitle(s, shop.id, shop.name)}</h2>
          <small>
            {t('shop.progress', { n: count, total: shop.upgrades.length })} ·{' '}
            {t('shop.rate', { rate: shopRate(shop.baseRatePerHour, count, economy.offlineSales.pctPerUpgrade) })}
          </small>
        </div>
        <div class="sheet-actions">
          {h.decorate && <DecorateButton d={h.decorate} />}
          <Button variant="teal" size="sm" onClick={h.onPlay} testId="shop-play">
            <Play size={18} /> {t('map.play')}
          </Button>
        </div>
      </div>
      <ul class="upg-list">
        {shop.upgrades.map((u) => {
          const st = upgradeState(s, shop.id, u.slot);
          const name = upgradeName(u.id, u.name);
          if (st.status === 'owned')
            return (
              <li class="upg owned" key={u.slot}>
                <Check />
                <span class="upg-name">{name}</span>
                <Sprite id={pieceId(shop.id, u.slot, st.style)} size={34} />
              </li>
            );
          if (st.status === 'locked')
            return (
              <li class="upg locked" key={u.slot}>
                <Lock />
                <span class="upg-name">{name}</span>
                <span class="upg-cost">
                  <Amount kind="coins" value={u.coins} size={16} />
                  {u.starsGate > 0 && <Amount kind="stars" value={u.starsGate} size={16} />}
                  {u.rareItem && <Sprite id={u.rareItem} size={18} />}
                </span>
              </li>
            );
          const sel = h.selected[u.slot] ?? 'a';
          const free = st.cost.coins === 0 && st.cost.stars === 0;
          return (
            <li class="upg next" key={u.slot} data-testid={`upg-${u.slot}`}>
              <div class="upg-top">
                <span class="upg-name">{name}</span>
                <div class="swatches" role="radiogroup" aria-label={t('shop.style')}>
                  {(['a', 'b', 'c'] as const).map((k) => (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={sel === k}
                      aria-label={t('shop.styleN', { s: k.toUpperCase() })}
                      class={`swatch ${sel === k ? 'sel' : ''}`}
                      onClick={() => h.onSelect(u.slot, k)}
                      key={k}
                    >
                      <Sprite id={pieceId(shop.id, u.slot, k)} size={42} />
                    </button>
                  ))}
                </div>
              </div>
              {u.rareItem && st.status === 'ready' && (
                <div class="rare-line">
                  <span>{t('restock.uses')}</span>
                  <RareChip id={u.rareItem} have />
                </div>
              )}
              {st.status === 'ready' ? (
                <Button variant="primary" size="md" pulse={free} disabled={h.building} onClick={() => h.onBuy(u.slot, sel)} testId="upg-buy">
                  {free ? (
                    t('shop.free')
                  ) : (
                    <>
                      {t('shop.build')} <Amount kind="coins" value={st.cost.coins} size={18} />
                    </>
                  )}
                </Button>
              ) : st.status === 'rare' ? (
                <div class="rare-need" data-testid="upg-rare">
                  <div class="rare-line">
                    <span>{t('restock.needs')}</span>
                    <RareChip id={st.rareItem} have={false} />
                  </div>
                  <Button variant="teal" size="md" onClick={() => h.onRestock(u.slot, st.rareItem)} testId="upg-restock">
                    {t('restock.get')}
                  </Button>
                </div>
              ) : (
                <div class="need" data-testid="upg-need">
                  <span>{t('shop.need')}</span>
                  {st.need.coins > 0 && <Amount kind="coins" value={st.need.coins} size={18} />}
                  {st.need.stars > 0 && <Amount kind="stars" value={st.need.stars} size={18} />}
                  {st.rareItem && <Sprite id={st.rareItem} size={20} />}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PlayBar({ p }: { p: PlayHud }): JSX.Element {
  return (
    <div class="playbar">
      <button type="button" class="icon-btn" aria-label={t('hud.pause')} onClick={p.onPause} data-testid="play-pause">
        <Pause />
      </button>
      <div class="level-pill" data-testid="play-level">
        {p.label ?? t('hud.level', { n: p.level })}
      </div>
      <div class="icon-spacer" />
    </div>
  );
}
